// Phase 3 — Admin File Browser. Read-only file index over R2, populated by
// the FileCamp migration. Three views:
//   • Sidebar of scopes/franchisees (counts + sizes)
//   • Centre tree-list of the current prefix
//   • Top search bar (whole-bucket name search)
//
// Migration panel lives at the top — Dry-Run / Commit / live progress.
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatDate } from "@/lib/date";
import {
  Folder, FolderOpen, File as FileIcon, ChevronRight, Search,
  Download, Loader2, AlertCircle, CloudUpload, Database, Users, Lock, Globe,
  RefreshCw, ChevronUp, ChevronDown, X, ExternalLink, Share2, Copy, CheckCircle2,
  FolderPlus, Trash2, Eye, LayoutGrid, List, FileText, Image as ImageIcon,
  FileAudio, FileVideo, FileArchive, Sparkles, Clock, PanelLeftClose, PanelLeftOpen,
  Megaphone,
} from "lucide-react";
import FolderActionsMenu from "@/components/files/FolderActionsMenu";
import FolderMovePicker from "@/components/files/FolderMovePicker";
import FolderShareModal from "@/components/files/FolderShareModal";
import RecentFilesStrip from "@/components/files/RecentFilesStrip";
import TrashView from "@/components/files/TrashView";
import FileThumbnail from "@/components/files/FileThumbnail";
import FilePreviewModal from "@/components/files/FilePreviewModal";
import FileReplaceButton from "@/components/files/FileReplaceButton";
import FileActionsMenu from "@/components/files/FileActionsMenu";
import { prettyFolderName } from "@/utils/folderName";

function fmtBytes(b) {
  if (b == null) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 ** 3) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 ** 3).toFixed(2)} GB`;
}

const SCOPE_BADGE = {
  franchisee: { label: "FRANCHISEE", icon: Users, cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  shared:     { label: "ALL",        icon: Globe, cls: "bg-blue-100 text-blue-800 border-blue-300" },
  admin:      { label: "ADMIN",      icon: Lock,  cls: "bg-amber-100 text-amber-900 border-amber-300" },
};

// Pick a sensible Lucide icon for a file based on its content_type / extension.
function fileIcon(file) {
  const ct = (file.content_type || "").toLowerCase();
  const ext = (file.name?.split(".").pop() || "").toLowerCase();
  if (ct.startsWith("image/") || ["jpg","jpeg","png","gif","webp","svg","heic"].includes(ext)) return ImageIcon;
  if (ct === "application/pdf" || ext === "pdf") return FileText;
  if (ct.startsWith("audio/") || ["mp3","wav","m4a","aac","ogg","flac","aif","aiff"].includes(ext)) return FileAudio;
  if (ct.startsWith("video/") || ["mp4","mov","webm","mkv","avi","wmv"].includes(ext)) return FileVideo;
  if (["zip","rar","7z","tar","gz"].includes(ext)) return FileArchive;
  if (["doc","docx","txt","rtf","md"].includes(ext) || ct.startsWith("text/")) return FileText;
  return FileIcon;
}

// Tailwind background tint per file kind — used by the grid thumbnail tiles
// so the eye can sort by type at a glance.
function fileTint(file) {
  const ct = (file.content_type || "").toLowerCase();
  const ext = (file.name?.split(".").pop() || "").toLowerCase();
  if (ct.startsWith("image/") || ["jpg","jpeg","png","gif","webp","svg","heic"].includes(ext)) return "bg-rose-50 text-rose-700 border-rose-200";
  if (ct === "application/pdf" || ext === "pdf") return "bg-red-50 text-red-700 border-red-200";
  if (ct.startsWith("audio/") || ["mp3","wav","m4a","aac","ogg","flac","aif","aiff"].includes(ext)) return "bg-purple-50 text-purple-700 border-purple-200";
  if (ct.startsWith("video/") || ["mp4","mov","webm","mkv","avi","wmv"].includes(ext)) return "bg-indigo-50 text-indigo-700 border-indigo-200";
  if (["doc","docx","txt","rtf","md"].includes(ext)) return "bg-sky-50 text-sky-700 border-sky-200";
  return "bg-stone-50 text-stone-600 border-stone-200";
}

function ScopeBadge({ scope }) {
  const s = SCOPE_BADGE[scope] || SCOPE_BADGE.admin;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border rounded-md ${s.cls}`}>
      <Icon className="w-2.5 h-2.5" /> {s.label}
    </span>
  );
}

function Breadcrumb({ prefix, onJump }) {
  const segs = useMemo(() => prefix.split("/").filter(Boolean), [prefix]);
  // Each segment is rendered as a clickable pill so it's obvious you can
  // tap any level to jump back. Active (last) segment is filled dark to
  // anchor the user's location, prior segments are outlined and hover to
  // a brand-green tint.
  const pillBase =
    "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#14532D]";
  const pillInactive = "bg-white border-stone-300 text-stone-700 hover:bg-[#14532D]/5 hover:border-[#14532D] hover:text-[#14532D]";
  const pillActive = "bg-stone-950 border-stone-950 text-white cursor-default";
  const atRoot = segs.length === 0;
  return (
    <nav
      aria-label="Folder path"
      data-testid="files-breadcrumb"
      className="flex items-center gap-2 flex-wrap bg-stone-50 border border-stone-200 rounded-2xl px-3 py-2.5"
    >
      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500 px-1 hidden sm:inline">Folder</span>
      <button
        type="button"
        onClick={() => atRoot ? null : onJump("")}
        aria-current={atRoot ? "page" : undefined}
        className={`${pillBase} ${atRoot ? pillActive : pillInactive}`}
      >
        <Folder className="w-4 h-4" />
        All Files
      </button>
      {segs.map((seg, i) => {
        const upto = segs.slice(0, i + 1).join("/") + "/";
        const isLast = i === segs.length - 1;
        return (
          <span key={i} className="flex items-center gap-2">
            <ChevronRight className="w-4 h-4 text-stone-400" />
            <button
              type="button"
              onClick={() => isLast ? null : onJump(upto)}
              aria-current={isLast ? "page" : undefined}
              className={`${pillBase} ${isLast ? pillActive : pillInactive}`}
            >
              {prettyFolderName(seg)}
            </button>
          </span>
        );
      })}
    </nav>
  );
}

// Collapsible "Per franchisee preview" — sits inside the migration plan.
// Defaults to collapsed because the franchisee list is long and Sandra
// usually only spot-checks one or two before committing.
function PerFranchiseePreview({ rows }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="per-franchisee-toggle"
        className="w-full px-3 py-2 bg-stone-50 hover:bg-stone-100 text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 border-b border-stone-200 flex items-center justify-between"
      >
        <span>Per franchisee preview <span className="text-stone-400 normal-case tracking-normal font-normal">({rows.length})</span></span>
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {open && (
        <div className="max-h-64 overflow-y-auto divide-y divide-stone-100 text-xs">
          {rows.map((f) => (
            <div key={f.franchisee_id} className="px-3 py-2 flex items-center justify-between hover:bg-stone-50">
              <span className="text-stone-700">{f.franchise_number || "—"} · {f.name || f.organisation || "(unnamed)"}</span>
              <span className="text-stone-500 tabular-nums">{f.files} files · {fmtBytes(f.bytes)}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function MigrationPanel({ db, onMigrationDone }) {
  const [planBusy, setPlanBusy] = useState(false);
  const [planErr, setPlanErr] = useState("");
  const [status, setStatus] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  // Derived: plan summary lives on status.latest_plan.summary
  const plan = status?.latest_plan?.summary || null;
  const planRunning = status?.plan?.running;

  const refreshStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/files/migration/status");
      setStatus(data);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    refreshStatus();
    const id = setInterval(refreshStatus, 4000);
    return () => clearInterval(id);
  }, [refreshStatus]);

  const dryRun = async () => {
    setPlanBusy(true); setPlanErr("");
    try {
      await api.post("/files/migration/plan");
      // The walk now runs in the background — status will reflect progress.
      refreshStatus();
    } catch (e) {
      setPlanErr(e?.response?.data?.detail || "Plan failed.");
    } finally { setPlanBusy(false); }
  };

  const commit = async () => {
    if (!window.confirm("Start the migration? Files will stream from FileCamp into R2 in the background. Safe to leave the page.")) return;
    try {
      await api.post("/files/migration/start?confirm=MIGRATE");
      refreshStatus();
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not start migration.");
    }
  };

  const running = status?.running;
  const pctFiles = status?.files_total ? Math.round((status.files_done / status.files_total) * 100) : 0;
  const pctBytes = status?.bytes_total ? Math.round((status.bytes_done / status.bytes_total) * 100) : 0;

  return (
    <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden mb-6" data-testid="migration-panel">
      <button onClick={() => setCollapsed((c) => !c)} className="w-full px-5 py-4 flex items-center justify-between hover:bg-stone-50">
        <div className="flex items-center gap-3">
          <CloudUpload className="w-4 h-4 text-stone-700" />
          <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-700">FileCamp → R2 Migration</span>
          {running && <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-md bg-amber-100 text-amber-900 border-amber-300 flex items-center gap-1"><Loader2 className="w-2.5 h-2.5 animate-spin" /> RUNNING</span>}
          {!running && status?.finished_at && <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-md bg-emerald-100 text-emerald-800 border-emerald-300">Last finished {formatDate(status.finished_at)}</span>}
        </div>
        {collapsed ? <ChevronDown className="w-4 h-4 text-stone-500" /> : <ChevronUp className="w-4 h-4 text-stone-500" />}
      </button>
      {!collapsed && (
        <div className="border-t border-stone-100 px-5 py-4 space-y-4">
          {running && status && (
            <div className="space-y-2" data-testid="migration-progress">
              <div className="flex items-center justify-between text-xs text-stone-700">
                <div className="flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {status.current ? <span className="truncate max-w-xl">{status.current}</span> : "Starting…"}</div>
                <div className="tabular-nums">{status.files_done}/{status.files_total} files · {fmtBytes(status.bytes_done)} of {fmtBytes(status.bytes_total)}</div>
              </div>
              <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pctBytes}%` }} />
              </div>
              <div className="text-[11px] text-stone-500 tabular-nums">{pctFiles}% by files · {pctBytes}% by bytes{status.errors ? ` · ${status.errors} errors` : ""}</div>
            </div>
          )}

          {!plan && !running && !planRunning && (
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-sm text-stone-800">Walk the FileCamp WebDAV server and plan which files will copy across, where each one will land in R2, and which franchisees they'll attach to.</div>
                <div className="text-xs text-stone-500 mt-1">Dry-run only — nothing is uploaded until you click Commit on the plan.</div>
              </div>
              <button onClick={dryRun} disabled={planBusy} data-testid="migration-dryrun"
                className="shrink-0 px-4 py-2 text-xs font-bold uppercase tracking-wider bg-stone-950 text-white hover:bg-stone-800 rounded-lg flex items-center gap-1.5 disabled:opacity-50">
                <RefreshCw className={`w-3.5 h-3.5 ${planBusy ? "animate-spin" : ""}`} /> {planBusy ? "Walking…" : "Run Dry-Run"}
              </button>
            </div>
          )}
          {planRunning && (
            <div className="flex items-center gap-2 text-sm text-stone-700" data-testid="plan-running">
              <Loader2 className="w-4 h-4 animate-spin" />
              Walking FileCamp WebDAV — this typically takes 30 — 90 seconds for 8 GB. Page will auto-refresh when the plan is ready.
            </div>
          )}
          {status?.plan?.error && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5" /> Plan error: {status.plan.error}
            </div>
          )}
          {planErr && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5" /> {planErr}
            </div>
          )}
          {plan && (
            <div className="space-y-3" data-testid="migration-plan">
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-stone-50 border border-stone-200 rounded-lg p-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Files</div>
                  <div className="font-display text-2xl text-stone-950 mt-1 tabular-nums" data-testid="plan-files">{plan.files_total.toLocaleString()}</div>
                </div>
                <div className="bg-stone-50 border border-stone-200 rounded-lg p-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Total Size</div>
                  <div className="font-display text-2xl text-stone-950 mt-1 tabular-nums">{fmtBytes(plan.bytes_total)}</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-700">Orphans</div>
                  <div className="font-display text-2xl text-amber-900 mt-1 tabular-nums">{plan.orphan_files}</div>
                </div>
                <div className="bg-stone-50 border border-stone-200 rounded-lg p-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Skipped (noise)</div>
                  <div className="font-display text-2xl text-stone-950 mt-1 tabular-nums">{plan.skipped}</div>
                </div>
              </div>
              <div className="border border-stone-200 rounded-lg overflow-hidden">
                <PerFranchiseePreview rows={plan.franchisee_summary || []} />
              </div>
              <div className="flex items-center justify-between pt-1">
                <button onClick={async () => {
                  await api.post("/files/migration/plan/discard").catch(() => {});
                  refreshStatus();
                }} className="text-xs text-stone-500 hover:text-stone-900" data-testid="plan-discard">Discard plan</button>
                <button onClick={commit} disabled={running} data-testid="migration-commit"
                  className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-[#dddd16] text-stone-950 hover:bg-[#aaaa11] rounded-lg flex items-center gap-1.5 disabled:opacity-50">
                  <CloudUpload className="w-3.5 h-3.5" /> Commit Migration ({plan.files_total.toLocaleString()} files · {fmtBytes(plan.bytes_total)})
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Preview modal — admin Files page delegates to the SHARED FilePreviewModal
// (same component used by the franchisee portal). Keeping a single source of
// truth here matters: the "Full page preview tries to save the file" bug
// recurred twice precisely because this page had its own duplicate modal
// that we kept forgetting to update. If you need to tweak preview rendering
// behaviour, edit `/app/frontend/src/components/files/FilePreviewModal.jsx`
// once and both surfaces inherit the fix.
function PreviewModal({ file, onClose }) {
  return <FilePreviewModal file={file} onClose={onClose} />;
}

// ---------------------------------------------------------------------------
// Share modal — creates a stable app-side share token that resolves to a
// fresh signed R2 URL on each click. Offers two durations matching how
// admins actually share files: a 6-month link for time-bounded campaigns
// (e-shots, sales PDFs to prospects) and a permanent "Forever" link for
// reference documents pinned into the Updates feed or shared with
// franchisees as a stable URL. R2's underlying signed URLs cap at 7 days
// so the redirect endpoint re-signs on every click.
function ShareModal({ file, onClose }) {
  // 30 days is the historic default; users can pick 6 months / Forever explicitly.
  const [days, setDays] = useState(30);
  const [url, setUrl] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");

  const generate = useCallback(async (d) => {
    setBusy(true); setErr(""); setUrl(null); setCopied(false);
    try {
      const { data } = await api.post("/files/share-link", { key: file.key, days: d });
      setUrl(data.url);
      setExpiresAt(data.expires_at);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not generate link."); }
    finally { setBusy(false); }
  }, [file]);

  useEffect(() => { if (file) generate(days); /* eslint-disable-next-line */ }, [file]);

  if (!file) return null;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch (e) { /* ignore */ }
  };

  return (
    <div onClick={onClose} className="fixed inset-0 z-[60] bg-stone-950/60 backdrop-blur-sm flex items-center justify-center p-6" data-testid="share-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200">
          <div className="flex items-center gap-2"><Share2 className="w-4 h-4 text-stone-700" />
            <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-700">Share Link</span>
          </div>
          <button onClick={onClose} data-testid="share-close" className="w-9 h-9 flex items-center justify-center hover:bg-stone-100 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <div className="text-sm font-semibold text-stone-950 truncate">{file.name}</div>
            <div className="text-[11px] text-stone-500 truncate font-mono">{file.key}</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500">Expires in</span>
            {[1, 7, 14, 30].map((d) => (
              <button key={d} onClick={() => { setDays(d); generate(d); }} data-testid={`share-days-${d}`}
                className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md border ${days === d ? "bg-stone-950 text-white border-stone-950" : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"}`}>
                {d} {d === 1 ? "day" : "days"}
              </button>
            ))}
            <button onClick={() => { setDays(180); generate(180); }} data-testid="share-days-180"
              className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md border ${days === 180 ? "bg-stone-950 text-white border-stone-950" : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"}`}>
              6 months
            </button>
            <button onClick={() => { setDays(0); generate(0); }} data-testid="share-days-lifetime"
              className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md border ${days === 0 ? "bg-stone-950 text-white border-stone-950" : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"}`}>
              Forever
            </button>
          </div>
          {busy && <div className="text-sm text-stone-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Generating…</div>}
          {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-lg">{err}</div>}
          {url && !busy && (
            <div className="space-y-2">
              <div className="flex items-stretch gap-2">
                <input readOnly value={url} data-testid="share-url"
                  className="flex-1 px-3 py-2 text-xs bg-stone-50 border border-stone-300 rounded-lg font-mono text-stone-700" />
                <button onClick={copy} data-testid="share-copy"
                  className="px-3 py-2 text-xs font-bold uppercase tracking-wider bg-[#dddd16] hover:bg-[#aaaa11] text-stone-950 rounded-lg flex items-center gap-1.5">
                  {copied ? <><CheckCircle2 className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                </button>
              </div>
              <div className="text-[11px] text-stone-500">
                Paste into an email or e-shot. Anyone with the link can open or download — no login required.
                {expiresAt
                  ? ` Auto-expires ${new Date(expiresAt).toLocaleString()}.`
                  : " This link never expires."}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Upload manager (drag-drop + button). Server-proxied multipart upload —
// the file is POSTed to /api/files/upload which streams the body to R2 and
// indexes it. This bypasses the need for R2 bucket CORS (our API token
// scope doesn't permit setting CORS).
const UploadButton = forwardRef(function UploadButton({ prefix, franchiseeId, onUploaded }, externalRef) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null); // { current, total, name, pct }
  // Sam's Feb 2026 bug: uploads fired from the "All Files" root
  // silently landed in ``admin/uploads/`` because both the frontend
  // and the backend defaulted an empty prefix. We refuse to fire the
  // request when the user hasn't picked a scope — the button is
  // disabled below AND the imperative handler bails out defensively
  // in case a drop event races the state.
  const uploadDisabled = !prefix;

  const uploadFile = useCallback(async (file, current, total) => {
    setProgress({ current, total, name: file.name, pct: 0 });
    const form = new FormData();
    form.append("file", file);
    form.append("prefix", prefix || "admin/uploads/");
    // Pass the exact franchisee_id whenever we're uploading inside a
    // franchisee folder. This bypasses the server's franchise-number
    // deduction (which is hard-blocked when the number is ambiguous)
    // and guarantees the files_index row is bound to the right record.
    if (franchiseeId) form.append("franchisee_id", franchiseeId);
    const backendBase = process.env.REACT_APP_BACKEND_URL || "";
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${backendBase}/api/files/upload`);
      xhr.withCredentials = true;
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          setProgress((p) => p ? { ...p, pct: Math.round((e.loaded / e.total) * 100) } : p);
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) return resolve();
        let msg = `Upload failed: ${xhr.status}`;
        try {
          const body = JSON.parse(xhr.responseText || "{}");
          if (body.detail) msg = body.detail;
        } catch { /* ignore parse error */ }
        reject(new Error(msg));
      };
      xhr.onerror = () => reject(new Error("Network error during upload"));
      xhr.send(form);
    });
  }, [prefix, franchiseeId]);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    if (uploadDisabled) {
      alert("Pick a folder from the sidebar first — files can't be uploaded to the All Files summary view.");
      return;
    }
    try {
      for (let i = 0; i < files.length; i++) {
        await uploadFile(files[i], i + 1, files.length);
      }
    } catch (e) {
      alert(`Upload failed: ${e.message}`);
    } finally {
      setProgress(null);
      onUploaded?.();
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  // Expose handleFiles to the parent FilesPage so its drag-and-drop
  // handler can route dropped files through the same upload pipeline
  // (progress UI, error handling, onUploaded callback) without duplicating
  // any logic.
  useImperativeHandle(externalRef, () => ({ handleFiles, isUploading: () => !!progress }), [progress, uploadFile, franchiseeId]);

  return (
    <>
      <input ref={inputRef} type="file" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)}
        data-testid="upload-input" />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={!!progress || uploadDisabled}
        data-testid="upload-btn"
        title={uploadDisabled ? "Pick a folder from the sidebar to upload into it." : "Upload files into this folder"}
        className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-stone-950 text-white hover:bg-stone-800 rounded-lg flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed">
        <CloudUpload className="w-3.5 h-3.5" />
        {progress ? `Uploading ${progress.current}/${progress.total} · ${progress.pct}%` : "Upload"}
      </button>
      {progress && (
        <div className="absolute mt-12 right-8 z-50 bg-white border border-stone-200 rounded-xl shadow-lg p-3 w-72" data-testid="upload-progress">
          <div className="text-xs text-stone-700 truncate mb-1.5">{progress.name}</div>
          <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progress.pct}%` }} />
          </div>
        </div>
      )}
    </>
  );
});

function NewFolderButton({ prefix, onCreated, onNavigate }) {
  const [busy, setBusy] = useState(false);
  const click = async () => {
    const name = window.prompt("Folder name:", "");
    if (!name) return;
    setBusy(true);
    try {
      const { data } = await api.post("/files/folder", { prefix, name });
      onCreated?.();
      // Auto-drill into the newly-created folder so the next Upload /
      // drop lands INSIDE it — matches Google Drive / Dropbox muscle
      // memory. Sam's Feb 2026 report: files were landing in
      // ``admin/uploads/`` because the prefix state never followed the
      // just-created folder.
      if (data?.folder_prefix && onNavigate) onNavigate(data.folder_prefix);
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not create folder.");
    } finally { setBusy(false); }
  };
  return (
    <button onClick={click} disabled={busy} data-testid="new-folder-btn"
      className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-stone-300 bg-white text-stone-900 hover:bg-stone-50 rounded-lg flex items-center gap-1.5 disabled:opacity-50">
      <FolderPlus className="w-3.5 h-3.5" /> {busy ? "Creating…" : "New folder"}
    </button>
  );
}

// Collapsible "Franchisees" scope panel — lives in the sidebar. Sandra
// rarely scopes to a single franchisee, so it defaults to collapsed and
// can stay closed to give the rest of the page room to breathe.
function FranchiseesScopePanel({ scopeTree, prefix, trashMode, onPick }) {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem("filesFranchiseesOpen") === "true"; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("filesFranchiseesOpen", String(open)); } catch { /* ignore */ }
  }, [open]);
  const list = scopeTree?.franchisees || [];
  return (
    <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="franchisees-panel">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="franchisees-panel-toggle"
        className="w-full px-3 py-2.5 flex items-center justify-between bg-stone-50 hover:bg-stone-100 border-b border-stone-200"
      >
        <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-700 inline-flex items-center gap-1.5">
          <Users className="w-3 h-3 text-emerald-700" /> Franchisees
          <span className="text-stone-400 normal-case tracking-normal font-normal">({list.length})</span>
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-stone-500" /> : <ChevronDown className="w-4 h-4 text-stone-500" />}
      </button>
      {open && (
        <div className="max-h-[60vh] overflow-y-auto">
          {list.length === 0 && (
            <div className="px-3 py-4 text-xs text-stone-500">No franchisees yet</div>
          )}
          {[...list]
            .sort((a, b) => {
              const an = parseInt(a.franchise_number, 10);
              const bn = parseInt(b.franchise_number, 10);
              if (isNaN(an) && isNaN(bn)) return 0;
              if (isNaN(an)) return 1;
              if (isNaN(bn)) return -1;
              return an - bn;
            })
            .map((f) => (
              <button
                key={f.franchisee_id}
                onClick={() => onPick(f.prefix || "franchisees/")}
                className={`w-full px-3 py-2 text-left text-xs hover:bg-stone-50 flex items-center gap-2 ${
                  prefix === (f.prefix || "franchisees/") && !trashMode ? "bg-emerald-50" : ""
                }`}
                data-testid={`scope-franchisee-${f.franchisee_id}`}
              >
                <Users className="w-3 h-3 text-emerald-600 shrink-0" />
                <span className="tabular-nums font-semibold text-stone-700 shrink-0">{f.franchise_number || "—"}</span>
                <span className="truncate flex-1 text-stone-600">{f.organisation || f.name}</span>
                <span className="text-[10px] text-stone-500 tabular-nums shrink-0">{f.files}</span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}


export default function FilesPage() {
  // Seed ``prefix`` from ``?prefix=`` so other pages can deep-link
  // into a specific folder (e.g. the Project Linking edit modal
  // surfaces "Open in Files" next to each search result). One-shot:
  // we read the param on mount only — subsequent in-page navigation
  // shouldn't have to push it back into the URL.
  const initialPrefix = (() => {
    try { return new URLSearchParams(window.location.search).get("prefix") || ""; }
    catch { return ""; }
  })();
  const [scopeTree, setScopeTree] = useState(null);
  const [tree, setTree] = useState(null);
  const [prefix, setPrefix] = useState(initialPrefix);
  // Derive the active franchisee_id whenever the user is browsing a
  // franchisees/<slug>/ scope. Passed to UploadButton so uploads inside
  // a franchisee folder carry the exact immutable franchisee_id and
  // never rely on server-side franchise-number deduction (which is now
  // hard-blocked for duplicated numbers — see franchisee_duplicate_guard).
  const activeFranchiseeId = (() => {
    if (!prefix || !prefix.startsWith("franchisees/") || !scopeTree?.franchisees) return null;
    // Match the longest prefix — the user may be deep inside a
    // sub-folder like franchisees/0095-.../Artwork/. We compare on the
    // sidebar franchisee's canonical prefix.
    const hit = scopeTree.franchisees.find((f) => f.prefix && prefix.startsWith(f.prefix));
    return hit?.franchisee_id || null;
  })();
  const [search, setSearch] = useState("");
  const [results, setResults] = useState(null);
  // Folder-scoped search — independent of the top-bar global search.
  // Only appears when the user has drilled into a sub-folder so they
  // can find content WITHIN that folder rather than across the whole
  // 1.7k-file bucket. Resets every time the active folder changes.
  const [folderSearch, setFolderSearch] = useState("");
  const [folderResults, setFolderResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState(null);
  const [preview, setPreview] = useState(null);
  const [share, setShare] = useState(null);
  const [folderShare, setFolderShare] = useState(null);
  const [movingFolder, setMovingFolder] = useState(null);
  // Single-file move state — uses the same FolderMovePicker as folder
  // moves but resolves to /files/move on confirm. Admin-only (gated
  // both in the UI and on the backend).
  const [movingFile, setMovingFile] = useState(null);
  // Multi-select for bulk move. Sam's Feb 2026 ask — moving files one
  // at a time from the file actions menu is slow when tidying a batch
  // of orphaned uploads. ``bulkKeys`` is the pending destination
  // selection state — non-null means the FolderMovePicker is open in
  // bulk mode.
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [bulkKeys, setBulkKeys] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const toggleSelected = useCallback((key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const clearSelection = useCallback(() => setSelectedKeys(new Set()), []);
  // Trash view: shows soft-deleted folders with restore + permanent
  // delete + empty-trash actions. Activated from the sidebar.
  const [trashMode, setTrashMode] = useState(false);
  // View mode persists per browser. "list" = compact admin rows; "grid" =
  // FileCamp-style large thumbnail tiles (better for franchisees / visual
  // browsing of artwork & PDFs).
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("filesViewMode") || "list"; }
    catch { return "list"; }
  });
  useEffect(() => {
    try { localStorage.setItem("filesViewMode", viewMode); } catch { /* ignore */ }
  }, [viewMode]);
  // Sidebar collapse — persisted. Lets the daily admin user fold the
  // scope/franchisee picker away so the main file pane goes full-width
  // (Sandra adds content here heavily; horizontal space matters).
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try { return localStorage.getItem("filesSidebarOpen") !== "false"; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("filesSidebarOpen", String(sidebarOpen)); } catch { /* ignore */ }
  }, [sidebarOpen]);

  const reloadScopes = useCallback(async () => {
    try { const { data } = await api.get("/files/scope-tree"); setScopeTree(data); }
    catch (e) { /* noop */ }
  }, []);
  const reloadTree = useCallback(async (p) => {
    setBusy(true);
    try {
      const { data } = await api.get("/files/tree", { params: { prefix: p } });
      setTree(data);
    } catch (e) { setTree({ folders: [], files: [], prefix: p }); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { reloadScopes(); }, [reloadScopes]);
  useEffect(() => { reloadTree(prefix); }, [prefix, reloadTree]);
  // Navigating between folders should clear stale selections so the
  // bulk-move toolbar never carries file keys from a folder the user
  // is no longer looking at.
  useEffect(() => { clearSelection(); }, [prefix, clearSelection]);

  const moveFolder = async (newParent) => {
    const src = movingFolder.key;
    await api.post("/files/folder/move", { prefix: src, new_parent: newParent });
    setMovingFolder(null);
    reloadTree(prefix); reloadScopes();
  };

  const moveFile = async (newParent) => {
    const src = movingFile.key;
    await api.post("/files/move", { key: src, new_parent: newParent });
    setMovingFile(null);
    reloadTree(prefix); reloadScopes();
  };

  // Bulk move — enforces the same-scope guardrail server-side so a
  // franchisee-owned file can never be moved into an admin/shared
  // folder (which would leave its ``franchisee_id`` binding dangling
  // and hide it from that franchisee). The backend returns per-key
  // results; we surface any failures in a small toast card.
  const bulkMove = async (newParent) => {
    const keys = Array.from(bulkKeys || []);
    if (keys.length === 0) { setBulkKeys(null); return; }
    const { data } = await api.post("/files/bulk-move", { keys, new_parent: newParent });
    setBulkKeys(null);
    clearSelection();
    reloadTree(prefix); reloadScopes();
    setBulkResult({ moved: data.moved || 0, failed: data.failed || 0, results: data.results || [] });
  };

  // Live search debounce
  useEffect(() => {
    if (search.trim().length < 2) { setResults(null); return; }
    const id = setTimeout(async () => {
      try {
        const { data } = await api.get("/files/search", { params: { q: search.trim(), limit: 80 } });
        setResults(data);
      } catch (e) { setResults({ items: [], count: 0 }); }
    }, 300);
    return () => clearTimeout(id);
  }, [search]);

  // Folder-scoped search debounce. Hits the SAME backend search endpoint
  // but pins ``prefix`` to the active folder so results are restricted to
  // files/folders beneath the user's current location. Reset when they
  // navigate away.
  useEffect(() => { setFolderSearch(""); setFolderResults(null); }, [prefix]);
  useEffect(() => {
    if (!prefix) { setFolderResults(null); return; }
    if (folderSearch.trim().length < 2) { setFolderResults(null); return; }
    const id = setTimeout(async () => {
      try {
        const { data } = await api.get("/files/search", { params: { q: folderSearch.trim(), prefix, limit: 100 } });
        setFolderResults(data);
      } catch (e) { setFolderResults({ items: [], folders: [], count: 0 }); }
    }, 300);
    return () => clearTimeout(id);
  }, [folderSearch, prefix]);

  const refreshSearch = useCallback(async () => {
    if (search.trim().length < 2) return;
    try {
      const { data } = await api.get("/files/search", { params: { q: search.trim(), limit: 80 } });
      setResults(data);
    } catch (e) { /* noop */ }
  }, [search]);

  const download = async (key) => {
    setDownloadingKey(key);
    try {
      const { data } = await api.get("/files/download", { params: { key } });
      window.open(data.url, "_blank");
    } catch (e) {
      alert(e?.response?.data?.detail || "Download link could not be generated.");
    } finally { setDownloadingKey(null); }
  };

  // ---- Drag-and-drop upload ------------------------------------------------
  // We let the user drop files onto the main content pane and route them
  // through the same UploadButton handler (progress UI, error handling).
  // A full-page overlay only appears while files are actively being
  // dragged over the window — otherwise it'd block hover-states on links.
  const uploadRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const dragCounter = useRef(0);

  useEffect(() => {
    if (trashMode) return; // never allow drop into trash view
    // Always preventDefault on drag/drop window-wide. Without this Chrome
    // navigates the tab away to display the dropped file (the symptom the
    // user saw — being "spit back to the top level" was actually the
    // browser opening the PDF as a new document).
    const hasFiles = (e) => {
      try {
        const t = e.dataTransfer?.types;
        if (!t) return false;
        for (let i = 0; i < t.length; i++) if (t[i] === "Files") return true;
        return false;
      } catch { return false; }
    };
    const onDragEnter = (e) => {
      e.preventDefault();
      if (!hasFiles(e)) return;
      dragCounter.current += 1;
      setDragActive(true);
    };
    const onDragOver = (e) => { e.preventDefault(); };
    const onDragLeave = (e) => {
      e.preventDefault();
      if (!hasFiles(e)) return;
      dragCounter.current = Math.max(0, dragCounter.current - 1);
      if (dragCounter.current === 0) setDragActive(false);
    };
    const onDrop = (e) => {
      e.preventDefault();
      dragCounter.current = 0;
      setDragActive(false);
      const files = e.dataTransfer?.files;
      if (!files || files.length === 0) return;
      uploadRef.current?.handleFiles(files);
    };
    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("drop", onDrop);
    };
  }, [trashMode]);

  return (
    <div className="min-h-screen bg-[#FBFAF8]" data-testid="files-page">
      {/* Drag overlay — shown only while a file is actively dragged over
          the window. Sits above everything so the user gets unambiguous
          feedback that they can drop. */}
      {dragActive && (
        <div
          data-testid="files-drop-overlay"
          className="fixed inset-0 z-[200] bg-stone-950/40 backdrop-blur-sm flex items-center justify-center pointer-events-none"
        >
          <div className={`bg-white border-4 border-dashed ${prefix ? "border-[#dddd16]" : "border-red-400"} rounded-3xl px-12 py-10 max-w-md text-center shadow-2xl`}>
            <CloudUpload className={`w-12 h-12 ${prefix ? "text-[#aaaa11]" : "text-red-500"} mx-auto mb-3`} />
            <div className="text-xl font-display font-black text-stone-950">
              {prefix ? "Drop files to upload" : "Pick a folder first"}
            </div>
            <div className="text-sm text-stone-600 mt-2">
              {prefix ? (
                <>They&apos;ll land in <span className="font-mono text-[12px] bg-stone-100 px-1.5 py-0.5 rounded">{prefix}</span></>
              ) : (
                <>The <b>All Files</b> summary view can&apos;t receive uploads. Navigate into a scope (Admin, Shared, or a Franchisee folder) from the sidebar first.</>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Topbar — heading + global search + sidebar toggle. Search lives
          here (not in the yellow panel) so it's always reachable and the
          yellow panel stays focused on folder actions. */}
      <div className="bg-white border-b border-stone-200 px-8 py-5 sticky top-0 z-10">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen((v) => !v)}
              data-testid="files-sidebar-toggle"
              title={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
              className="px-2.5 py-2 border border-stone-300 hover:bg-stone-50 rounded-lg text-stone-600 hover:text-stone-900"
            >
              {sidebarOpen ? <PanelLeftClose className="w-4 h-4" /> : <PanelLeftOpen className="w-4 h-4" />}
            </button>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-500">CRM · PHASE 3</div>
              <h1 className="font-display text-3xl text-stone-950 mt-1 flex items-baseline gap-3">
                Files
                {scopeTree && (
                  <span className="text-sm text-stone-500 tabular-nums font-normal">
                    {scopeTree.totals.files.toLocaleString()} files · {fmtBytes(scopeTree.totals.bytes)}
                  </span>
                )}
              </h1>
            </div>
          </div>
          <div className="flex-1 min-w-[260px] max-w-xl ml-auto">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search every file you can access by name (e.g. cat, halloween, stencil)…"
                data-testid="files-search"
                className="w-full pl-11 pr-9 py-3 bg-stone-50 border border-stone-300 text-sm focus:outline-none focus:border-stone-900 focus:bg-white rounded-xl" />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700"><X className="w-3.5 h-3.5" /></button>
              )}
            </div>
          </div>
          <Link to="/admin/announcements"
            data-testid="files-send-update"
            title="Send a branded Update to franchisees"
            className="ml-2 px-3 py-2 text-xs font-bold uppercase tracking-wider bg-stone-950 text-[#dddd16] hover:bg-stone-800 rounded-lg flex items-center gap-1.5 whitespace-nowrap">
            <Megaphone className="w-3.5 h-3.5" /> Send Update
          </Link>
        </div>
      </div>

      <div className="p-8 pt-6">
        {/* Migration panel removed from UI — migration completed (1,705 files, 7.2 GB).
            Backend endpoints under /api/admin/migration/* remain available for ad-hoc
            re-runs via the admin if ever needed. */}

        <div className={`grid gap-6 ${sidebarOpen ? "grid-cols-12" : "grid-cols-1"}`}>
          {/* Sidebar — scope navigation. Collapsible. */}
          {sidebarOpen && (
          <aside className="col-span-12 md:col-span-3 space-y-4" data-testid="files-sidebar">
            <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
              <button onClick={() => { setTrashMode(false); setPrefix(""); }} data-testid="scope-all"
                className={`w-full px-3 py-2 text-left text-xs font-bold uppercase tracking-wider hover:bg-stone-50 ${prefix === "" && !trashMode ? "bg-stone-100" : ""}`}>
                All files
              </button>
              <div className="border-t border-stone-100">
                <div className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 bg-stone-50">Shared</div>
                {(scopeTree?.shared_folders || []).map((f) => (
                  <button key={f.folder} onClick={() => { setTrashMode(false); setPrefix(`shared/${f.folder}/`); }}
                    className={`w-full px-3 py-2 text-left text-xs hover:bg-stone-50 flex items-center justify-between ${prefix === `shared/${f.folder}/` && !trashMode ? "bg-blue-50" : ""}`}>
                    <span className="flex items-center gap-2 truncate"><Globe className="w-3 h-3 text-blue-600" /> {prettyFolderName(f.folder)}</span>
                    <span className="text-[10px] text-stone-500 tabular-nums">{f.files}</span>
                  </button>
                ))}
              </div>
              <div className="border-t border-stone-100">
                <div className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 bg-stone-50">Admin only</div>
                {(scopeTree?.admin_folders || []).map((f) => (
                  <button key={f.folder} onClick={() => { setTrashMode(false); setPrefix(`admin/${f.folder}/`); }}
                    className={`w-full px-3 py-2 text-left text-xs hover:bg-stone-50 flex items-center justify-between ${prefix === `admin/${f.folder}/` && !trashMode ? "bg-amber-50" : ""}`}>
                    <span className="flex items-center gap-2 truncate"><Lock className="w-3 h-3 text-amber-600" /> {prettyFolderName(f.folder)}</span>
                    <span className="text-[10px] text-stone-500 tabular-nums">{f.files}</span>
                  </button>
                ))}
              </div>
              <div className="border-t border-stone-100">
                <button onClick={() => setTrashMode(true)} data-testid="scope-trash"
                  className={`w-full px-3 py-2 text-left text-xs font-bold uppercase tracking-wider hover:bg-stone-50 flex items-center gap-1.5 ${trashMode ? "bg-red-50 text-red-700" : "text-stone-700"}`}>
                  <Trash2 className="w-3 h-3" /> Trash
                </button>
              </div>
            </div>
            <FranchiseesScopePanel
              scopeTree={scopeTree}
              prefix={prefix}
              trashMode={trashMode}
              onPick={(p) => { setTrashMode(false); setPrefix(p); }}
            />
          </aside>
          )}

          {/* Main pane — full-width when sidebar is collapsed. */}
          <main className={`${sidebarOpen ? "col-span-12 md:col-span-9" : "col-span-12"} space-y-3`}>
            {trashMode ? (
              <TrashView onClose={() => setTrashMode(false)}
                onChanged={() => { reloadScopes(); reloadTree(prefix); }} />
            ) : results ? (
              <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="search-results">
                <div className="px-4 py-3 border-b border-stone-200 flex items-center justify-between">
                  <span className="text-xs uppercase tracking-widest font-bold text-stone-700">Search results · {results.count}</span>
                  <button onClick={() => setSearch("")} className="text-xs text-stone-500 hover:text-stone-900">Clear</button>
                </div>
                {results.items.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-stone-500">No files found.</div>
                ) : (
                  <div className="divide-y divide-stone-100">
                    {results.items.map((it) => (
                      <div key={it.key} className="px-4 py-2 flex items-center justify-between hover:bg-stone-50">
                        <button onClick={() => setPreview(it)} className="flex items-center gap-3 truncate text-left flex-1" data-testid={`preview-${it.key}`}>
                          <FileIcon className="w-3.5 h-3.5 text-stone-500 shrink-0" />
                          <div className="truncate">
                            <div className="text-sm text-stone-900 truncate hover:underline">{it.name}</div>
                            <div className="text-[11px] text-stone-500 truncate font-mono">{it.parent_prefix}</div>
                          </div>
                        </button>
                        <div className="flex items-center gap-2">
                          <ScopeBadge scope={it.scope} />
                          <span className="text-xs text-stone-500 tabular-nums">{fmtBytes(it.size)}</span>
                          <button
                            onClick={() => { setPrefix(it.parent_prefix || ""); setSearch(""); setResults(null); }}
                            title="Reveal this file in its folder"
                            data-testid={`open-folder-${it.key}`}
                            className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider bg-white border border-stone-300 hover:bg-stone-50 text-stone-800 rounded-md flex items-center gap-1"
                          >
                            <FolderOpen className="w-3 h-3" /> Open Folder
                          </button>
                          <button onClick={() => setShare(it)} title="Share link"
                            className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider bg-white border border-stone-300 hover:bg-stone-50 text-stone-700 rounded-md flex items-center gap-1">
                            <Share2 className="w-3 h-3" />
                          </button>
                          <button onClick={() => download(it.key)} disabled={downloadingKey === it.key}
                            className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider bg-stone-950 text-white hover:bg-stone-800 rounded-md flex items-center gap-1 disabled:opacity-50">
                            {downloadingKey === it.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Download
                          </button>
                          <FileActionsMenu
                            file={it}
                            onChanged={() => { refreshSearch(); reloadScopes(); }}
                            onMove={(f) => setMovingFile(f)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <>
                <RecentFilesStrip
                  onOpenFile={(it) => setPreview(it)}
                  onDownload={(key) => download(key)}
                  onOpenFolder={(key) => setPrefix(key)} />
                <div className="bg-[#EEEE86] border border-stone-300 rounded-2xl px-5 py-4 flex items-center justify-between gap-3 flex-wrap shadow-sm" data-testid="files-tree">
                  <Breadcrumb prefix={prefix} onJump={setPrefix} />
                  <div className="flex items-center gap-2 relative flex-wrap">
                    {/* View mode toggle — list/grid */}
                    <div className="flex items-center bg-stone-100 rounded-lg p-0.5" data-testid="view-toggle">
                      <button onClick={() => setViewMode("list")} data-testid="view-list"
                        title="List view"
                        className={`px-2 py-1 rounded-md flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider transition-all ${viewMode === "list" ? "bg-white text-stone-950 shadow-sm" : "text-stone-500 hover:text-stone-900"}`}>
                        <List className="w-3.5 h-3.5" /> List
                      </button>
                      <button onClick={() => setViewMode("grid")} data-testid="view-grid"
                        title="Grid view"
                        className={`px-2 py-1 rounded-md flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider transition-all ${viewMode === "grid" ? "bg-white text-stone-950 shadow-sm" : "text-stone-500 hover:text-stone-900"}`}>
                        <LayoutGrid className="w-3.5 h-3.5" /> Grid
                      </button>
                    </div>
                    <NewFolderButton
                      prefix={prefix}
                      onCreated={() => { reloadTree(prefix); reloadScopes(); }}
                      onNavigate={(p) => setPrefix(p)}
                    />
                    <UploadButton
                      ref={uploadRef}
                      prefix={prefix}
                      franchiseeId={activeFranchiseeId}
                      onUploaded={() => { reloadTree(prefix); reloadScopes(); }}
                    />
                    {/* Folder-scoped search — only renders when the user has
                        drilled into a sub-folder. Saves them sifting through
                        the 1.7k-file global search when they know roughly where
                        the file lives. */}
                    {prefix && (
                      <div className="relative flex-1 min-w-[200px] max-w-[420px]" data-testid="folder-search-wrap">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-500 pointer-events-none" />
                        <input
                          type="search"
                          value={folderSearch}
                          onChange={(e) => setFolderSearch(e.target.value)}
                          placeholder="Search in this folder…"
                          data-testid="folder-search-input"
                          className="w-full pl-8 pr-8 py-1.5 text-sm bg-white border border-stone-300 focus:border-stone-950 focus:outline-none rounded-md placeholder:text-stone-400"
                        />
                        {folderSearch && (
                          <button
                            type="button"
                            onClick={() => setFolderSearch("")}
                            data-testid="folder-search-clear"
                            aria-label="Clear folder search"
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-900">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {folderResults ? (
                  <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="folder-search-results">
                    <div className="px-4 py-2.5 bg-stone-50 border-b border-stone-200 flex items-center justify-between gap-2 text-xs">
                      <span className="text-stone-700">
                        <span className="font-semibold">{(folderResults.items || []).length + (folderResults.folders || []).length}</span>
                        {" "}match{((folderResults.items || []).length + (folderResults.folders || []).length) === 1 ? "" : "es"} for{" "}
                        <span className="font-mono">&quot;{folderSearch}&quot;</span> in this folder
                      </span>
                      <button
                        onClick={() => setFolderSearch("")}
                        data-testid="folder-search-results-clear"
                        className="text-stone-500 hover:text-stone-900 underline">
                        clear
                      </button>
                    </div>
                    {(folderResults.items || []).length === 0 && (folderResults.folders || []).length === 0 ? (
                      <div className="px-4 py-12 text-center">
                        <Search className="w-8 h-8 text-stone-300 mx-auto mb-2" />
                        <div className="text-sm text-stone-500">No matches in this folder. Try the global search at the top.</div>
                      </div>
                    ) : viewMode === "grid" ? (
                      <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3" data-testid="folder-search-grid">
                        {(folderResults.folders || []).map((f) => (
                          <div key={`fr-${f.prefix}`} className="group flex flex-col items-stretch border border-stone-200 hover:border-stone-400 hover:shadow-md transition-all rounded-xl overflow-hidden text-left bg-white relative">
                            <button onClick={() => setPrefix(f.prefix)}
                              data-testid={`folder-search-folder-grid-${f.name}`}
                              className="aspect-square bg-[#dddd16]/15 flex items-center justify-center border-b border-[#dddd16]/30">
                              <Folder className="w-14 h-14 text-[#14532D] group-hover:scale-105 transition-transform" />
                            </button>
                            <div className="p-2.5">
                              <button onClick={() => setPrefix(f.prefix)} className="text-left min-w-0 w-full">
                                <div className="text-xs font-semibold text-stone-900 truncate">{prettyFolderName(f.name)}</div>
                                <div className="text-[10px] text-stone-500 truncate mt-0.5" title={f.prefix}>{f.prefix}</div>
                              </button>
                            </div>
                          </div>
                        ))}
                        {(folderResults.items || []).map((it) => (
                          <div key={`fr-${it.key}`} className="group flex flex-col items-stretch border border-stone-200 hover:border-stone-400 hover:shadow-md transition-all rounded-xl overflow-hidden bg-white relative" data-testid={`folder-search-file-grid-${it.key}`}>
                            <button onClick={() => setPreview(it)} className="aspect-square overflow-hidden">
                              <FileThumbnail file={it} className="w-full h-full" />
                            </button>
                            <div className="p-2.5 flex-1 flex flex-col">
                              <div className="text-xs font-semibold text-stone-900 truncate" title={it.name}>{it.name}</div>
                              <div className="text-[10px] text-stone-500 truncate mt-0.5" title={it.key}>{it.key}</div>
                              <div className="flex items-center justify-between mt-1">
                                <span className="text-[10px] text-stone-500 tabular-nums">{fmtBytes(it.size)}</span>
                              </div>
                              <div className="flex items-center gap-1 mt-2">
                                <button onClick={() => setShare(it)}
                                  title="Share link"
                                  className="flex-1 px-1.5 py-1 text-[9px] font-bold uppercase tracking-wider bg-white border border-stone-300 hover:bg-stone-50 text-stone-700 rounded-md flex items-center justify-center gap-1">
                                  <Share2 className="w-3 h-3" /> Share
                                </button>
                                <button onClick={() => download(it.key)} disabled={downloadingKey === it.key}
                                  className="flex-1 px-1.5 py-1 text-[9px] font-bold uppercase tracking-wider bg-stone-950 text-white hover:bg-stone-800 rounded-md flex items-center justify-center gap-1 disabled:opacity-50">
                                  {downloadingKey === it.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="divide-y divide-stone-100">
                        {(folderResults.folders || []).map((f) => (
                          <button
                            key={`fr-${f.prefix}`}
                            type="button"
                            onClick={() => setPrefix(f.prefix)}
                            data-testid={`folder-search-folder-${f.name}`}
                            className="w-full px-4 py-2 hover:bg-stone-50 flex items-center justify-between gap-3 text-left">
                            <div className="flex items-center gap-3 truncate min-w-0">
                              <Folder className="w-4 h-4 text-[#14532D] shrink-0" />
                              <span className="text-sm text-stone-900 truncate">{prettyFolderName(f.name)}</span>
                            </div>
                            <span className="text-[10px] text-stone-500 truncate max-w-[50%]" title={f.prefix}>{f.prefix}</span>
                          </button>
                        ))}
                        {(folderResults.items || []).map((it) => (
                          <div key={`fr-${it.key}`} className="px-4 py-2 flex items-center justify-between hover:bg-stone-50" data-testid={`folder-search-file-${it.key}`}>
                            <button onClick={() => setPreview(it)} className="flex items-center gap-3 truncate text-left flex-1 min-w-0">
                              <FileIcon className="w-3.5 h-3.5 text-stone-500 shrink-0" />
                              <div className="min-w-0 flex-1">
                                <div className="text-sm text-stone-900 truncate hover:underline">{it.name}</div>
                                <div className="text-[10px] text-stone-500 truncate" title={it.key}>{it.key}</div>
                              </div>
                            </button>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="text-xs text-stone-500 tabular-nums">{fmtBytes(it.size)}</span>
                              <button onClick={() => setShare(it)} title="Share link"
                                className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider bg-white border border-stone-300 hover:bg-stone-50 text-stone-700 rounded-md flex items-center gap-1">
                                <Share2 className="w-3 h-3" />
                              </button>
                              <button onClick={() => download(it.key)} disabled={downloadingKey === it.key}
                                className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider bg-stone-950 text-white hover:bg-stone-800 rounded-md flex items-center gap-1 disabled:opacity-50">
                                {downloadingKey === it.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Download
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
                  {selectedKeys.size > 0 && (
                    <div
                      className="px-4 py-2.5 flex items-center justify-between gap-3 bg-emerald-50 border-b border-emerald-200"
                      data-testid="bulk-actions-toolbar"
                    >
                      <div className="text-xs font-bold text-emerald-900 uppercase tracking-wider">
                        {selectedKeys.size} file{selectedKeys.size === 1 ? "" : "s"} selected
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setBulkKeys(new Set(selectedKeys))}
                          data-testid="bulk-move-btn"
                          className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider bg-stone-950 text-white hover:bg-stone-800 rounded-md flex items-center gap-1"
                        >
                          Move to folder…
                        </button>
                        <button
                          type="button"
                          onClick={clearSelection}
                          data-testid="bulk-clear-btn"
                          className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider bg-white border border-stone-300 text-stone-700 hover:bg-stone-50 rounded-md"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  )}
                  {busy && (
                    <div className="px-4 py-8 text-center text-sm text-stone-500 flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                    </div>
                  )}
                  {!busy && tree && tree.folders.length === 0 && tree.files.length === 0 && (
                    <div className="px-4 py-12 text-center" data-testid="files-empty">
                      <Database className="w-8 h-8 text-stone-300 mx-auto mb-2" />
                      <div className="text-sm text-stone-500 max-w-md mx-auto">
                        {scopeTree?.totals.files === 0
                          ? "No files in R2 yet. Drop files into a franchisee folder to get started."
                          : prefix === ""
                            ? "\u2018All Files\u2019 is a summary view — pick a scope (Shared, Admin, or a Franchisee folder) from the sidebar to see its contents."
                            : "This folder is empty."}
                      </div>
                    </div>
                  )}
                  {!busy && tree && (tree.folders.length > 0 || tree.files.length > 0) && (
                    viewMode === "grid" ? (
                      <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3" data-testid="files-grid">
                        {tree.folders.map((f) => (
                          <div key={f.key} className="group flex flex-col items-stretch border border-stone-200 hover:border-stone-400 hover:shadow-md transition-all rounded-xl overflow-hidden text-left bg-white relative">
                            <button onClick={() => setPrefix(f.key)} data-testid={`folder-grid-${f.name}`}
                              className="aspect-square bg-[#dddd16]/15 flex items-center justify-center border-b border-[#dddd16]/30">
                              <Folder className="w-14 h-14 text-[#14532D] group-hover:scale-105 transition-transform" />
                            </button>
                            <div className="p-2.5 flex items-start justify-between gap-2">
                              <button onClick={() => setPrefix(f.key)} className="text-left min-w-0 flex-1">
                                <div className="text-xs font-semibold text-stone-900 truncate">{prettyFolderName(f.name)}</div>
                                <div className="text-[10px] text-stone-500 tabular-nums mt-0.5">{f.files} files · {fmtBytes(f.bytes)}</div>
                              </button>
                              <FolderActionsMenu folder={f}
                                onChanged={() => { reloadTree(prefix); reloadScopes(); }}
                                onMove={(fl) => setMovingFolder(fl)}
                                onShare={(fl) => setFolderShare(fl)} />
                            </div>
                          </div>
                        ))}
                        {tree.files.map((it) => {
                          return (
                            <div key={it.key} className="group flex flex-col items-stretch border border-stone-200 hover:border-stone-400 hover:shadow-md transition-all rounded-xl overflow-hidden bg-white relative" data-testid={`file-grid-${it.key}`}>
                              <button onClick={() => setPreview(it)} data-testid={`preview-grid-${it.key}`}
                                className="aspect-square overflow-hidden">
                                <FileThumbnail file={it} className="w-full h-full" />
                              </button>
                              <div className="p-2.5 flex-1 flex flex-col">
                                <div className="text-xs font-semibold text-stone-900 truncate" title={it.name}>{it.name}</div>
                                <div className="flex items-center justify-between mt-1">
                                  <span className="text-[10px] text-stone-500 tabular-nums">{fmtBytes(it.size)}</span>
                                  <ScopeBadge scope={it.scope} />
                                </div>
                                <div className="flex items-center gap-1 mt-2">
                                  <button onClick={() => setShare(it)} data-testid={`share-grid-${it.key}`}
                                    title="Share link"
                                    className="flex-1 px-1.5 py-1 text-[9px] font-bold uppercase tracking-wider bg-white border border-stone-300 hover:bg-stone-50 text-stone-700 rounded-md flex items-center justify-center gap-1">
                                    <Share2 className="w-3 h-3" /> Share
                                  </button>
                                  <FileReplaceButton
                                    file={it}
                                    onReplaced={() => { reloadTree(prefix); reloadScopes(); }}
                                    className="flex-1 px-1.5 py-1 text-[9px] font-bold uppercase tracking-wider bg-white border border-stone-300 hover:bg-stone-50 text-stone-700 rounded-md flex items-center justify-center gap-1 disabled:opacity-50"
                                  />
                                  <button onClick={() => download(it.key)} disabled={downloadingKey === it.key}
                                    data-testid={`download-grid-${it.key}`}
                                    className="flex-1 px-1.5 py-1 text-[9px] font-bold uppercase tracking-wider bg-stone-950 text-white hover:bg-stone-800 rounded-md flex items-center justify-center gap-1 disabled:opacity-50">
                                    {downloadingKey === it.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                                  </button>
                                  <FileActionsMenu
                                    file={it}
                                    onChanged={() => { reloadTree(prefix); reloadScopes(); }}
                                    onMove={(f) => setMovingFile(f)}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                    <div className="divide-y divide-stone-100">
                      {tree.folders.map((f) => (
                        <div key={f.key} data-testid={`folder-${f.name}`}
                          className="w-full px-4 py-2 hover:bg-stone-50 flex items-center justify-between gap-3 group">
                          <button onClick={() => setPrefix(f.key)}
                            className="flex items-center gap-3 truncate text-left flex-1">
                            <Folder className="w-4 h-4 text-[#14532D] shrink-0" />
                            <span className="text-sm text-stone-900 truncate">{prettyFolderName(f.name)}</span>
                          </button>
                          <span className="text-xs text-stone-500 tabular-nums">{f.files} files · {fmtBytes(f.bytes)}</span>
                          <FolderActionsMenu folder={f}
                            onChanged={() => { reloadTree(prefix); reloadScopes(); }}
                            onMove={(fl) => setMovingFolder(fl)}
                            onShare={(fl) => setFolderShare(fl)} />
                        </div>
                      ))}
                      {tree.files.map((it) => (
                        <div key={it.key} className={`px-4 py-2 flex items-center justify-between hover:bg-stone-50 ${selectedKeys.has(it.key) ? "bg-emerald-50" : ""}`} data-testid={`file-row-${it.key}`}>
                          <label
                            className="flex items-center pr-2 cursor-pointer"
                            data-testid={`file-select-${it.key}`}
                            title="Select for bulk actions"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded border-stone-400 text-stone-900 focus:ring-stone-900"
                              checked={selectedKeys.has(it.key)}
                              onChange={() => toggleSelected(it.key)}
                              aria-label={`Select ${it.name}`}
                            />
                          </label>
                          <button onClick={() => setPreview(it)} className="flex items-center gap-3 truncate text-left flex-1" data-testid={`preview-file-${it.key}`}>
                            <FileIcon className="w-3.5 h-3.5 text-stone-500 shrink-0" />
                            <span className="text-sm text-stone-900 truncate hover:underline">{it.name}</span>
                            {it.orphan && <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-900 border border-amber-300 rounded-md">orphan</span>}
                          </button>
                          <div className="flex items-center gap-2">
                            <ScopeBadge scope={it.scope} />
                            <span className="text-xs text-stone-500 tabular-nums">{fmtBytes(it.size)}</span>
                            <button onClick={() => setShare(it)} title="Share link" data-testid={`share-btn-${it.key}`}
                              className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider bg-white border border-stone-300 hover:bg-stone-50 text-stone-700 rounded-md flex items-center gap-1">
                              <Share2 className="w-3 h-3" />
                            </button>
                            <FileReplaceButton file={it} onReplaced={() => { reloadTree(prefix); reloadScopes(); }} />
                            <button onClick={() => download(it.key)} disabled={downloadingKey === it.key}
                              className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider bg-stone-950 text-white hover:bg-stone-800 rounded-md flex items-center gap-1 disabled:opacity-50">
                              {downloadingKey === it.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Download
                            </button>
                            <FileActionsMenu
                              file={it}
                              onChanged={() => { reloadTree(prefix); reloadScopes(); }}
                              onMove={(f) => setMovingFile(f)}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    )
                  )}
                </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>

      {/* Preview & Share modals */}
      <PreviewModal file={preview} onClose={() => setPreview(null)} />
      <ShareModal file={share} onClose={() => setShare(null)} />
      <FolderShareModal folder={folderShare} onClose={() => setFolderShare(null)} />
      <FolderMovePicker open={!!movingFolder}
        sourcePrefix={movingFolder?.key || ""}
        onClose={() => setMovingFolder(null)}
        onConfirm={moveFolder} />
      <FolderMovePicker open={!!movingFile}
        sourcePrefix={movingFile?.parent_prefix || ""}
        onClose={() => setMovingFile(null)}
        onConfirm={moveFile} />
      <FolderMovePicker open={!!bulkKeys}
        sourcePrefix={prefix || ""}
        onClose={() => setBulkKeys(null)}
        onConfirm={bulkMove} />
      {bulkResult && (
        <div
          data-testid="bulk-result-toast"
          className="fixed bottom-6 right-6 z-[300] max-w-sm bg-white border border-stone-200 rounded-xl shadow-xl p-4"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-bold text-stone-950">
              Moved {bulkResult.moved} · Failed {bulkResult.failed}
            </div>
            <button
              type="button"
              onClick={() => setBulkResult(null)}
              className="text-stone-400 hover:text-stone-900 text-lg leading-none"
              aria-label="Dismiss"
              data-testid="bulk-result-close"
            >×</button>
          </div>
          {bulkResult.failed > 0 && (
            <ul className="text-[11px] text-red-700 space-y-1 max-h-32 overflow-auto">
              {bulkResult.results.filter((r) => !r.ok).slice(0, 8).map((r) => (
                <li key={r.key} className="truncate" title={`${r.key} — ${r.error}`}>
                  {r.key.split("/").pop()}: {r.error}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
